import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { applyCustomerRounding, computeQuoteTotals, formatMoney, lineExtension } from "./quotePricingMath";
import { groupQuoteOwn, rollupPlannerQuotes } from "./plannerQuoteRates";
import { customerPurchaseBreakdownFromWixComponents, customerQuotePresentationFromComponents, internalQuoteEconomicsFromComponents, robotCostFromWixComponents } from "./wixJobQuote";

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const CUSTOMER_CONTENT_BOTTOM = 268;
const CUSTOMER_PAGE_TOP = 16;
const INTERNAL_CONTENT_BOTTOM = 276;
const INTERNAL_PAGE_TOP = 18;
const PDF_FONT = "K2D";
const PDF_LOGO_URL = "/pbr-logo-light.png";
const DEFAULT_CUSTOMER_DISCLAIMER =
    "Preliminary, non-binding estimate only. This document is for planning purposes and is not a formal quotation, offer, proposal, contract, or commitment to perform work. Pricing, scope, schedule, and customer-provided equipment assumptions are fully variable and subject to adjustment. Travel expenses are not included. A binding agreement requires a formal written quotation or proposal accepted in writing by both parties and, where applicable, a mutually executed contract.";
let k2dFontDataPromise;
let logoDataUrlPromise;

function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}
async function loadK2DFont(doc) {
    try {
        k2dFontDataPromise ||= Promise.all(
            ["K2D-Regular.ttf", "K2D-Bold.ttf"].map((filename) =>
                fetch(`https://raw.githubusercontent.com/google/fonts/main/ofl/k2d/${filename}`)
                    .then((response) => {
                        if (!response.ok) throw new Error("K2D font unavailable");
                        return response.arrayBuffer();
                    })
                    .then(arrayBufferToBase64)
            )
        );
        const [regularFontData, boldFontData] = await k2dFontDataPromise;
        doc.addFileToVFS("K2D-Regular.ttf", regularFontData);
        doc.addFileToVFS("K2D-Bold.ttf", boldFontData);
        doc.addFont("K2D-Regular.ttf", PDF_FONT, "normal");
        doc.addFont("K2D-Bold.ttf", PDF_FONT, "bold");
        return PDF_FONT;
    } catch {
        return "helvetica";
    }
}

async function loadPdfLogoDataUrl() {
    try {
        logoDataUrlPromise ||= fetch(PDF_LOGO_URL)
            .then((response) => {
                if (!response.ok) throw new Error("Logo unavailable");
                return response.blob();
            })
            .then(
                (blob) =>
                    new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    })
            );
        return await logoDataUrlPromise;
    } catch {
        logoDataUrlPromise = null;
        return null;
    }
}

/** Draw the light brand mark. widthMm controls size; returns bottom Y of the logo box. */
function drawPdfLogo(doc, dataUrl, { x = MARGIN, y = 12, widthMm = 52 } = {}) {
    if (!dataUrl) return y;
    // pbr-logo-light.png is 2172×724
    const heightMm = widthMm * (724 / 2172);
    doc.addImage(dataUrl, "PNG", x, y, widthMm, heightMm);
    return y + heightMm;
}

function text(value, fallback = "—") {
    const result = value == null ? "" : String(value).trim();
    return result || fallback;
}

/** Company account + primary contact for customer-facing PDF headers. */
export function buildCustomerPdfDetails({ customer = null, customerName = "", contacts = [] } = {}) {
    const record = customer && typeof customer === "object" ? customer : {};
    const companyName = text(record.name || customerName, "");
    const contactRows = Array.isArray(contacts) ? contacts : [];
    const primaryFromRecord = text(record.primary_contact_name, "");
    const primaryFromContacts =
        contactRows
            .map((row) => text(row?.name, ""))
            .find((name) => name && name.toLowerCase() !== companyName.toLowerCase()) || "";
    const preparedFor =
        text(record.prepared_for || record.contact_name || record.contactName, "") ||
        (primaryFromRecord && primaryFromRecord.toLowerCase() !== companyName.toLowerCase()
            ? primaryFromRecord
            : primaryFromContacts);

    return {
        company: companyName,
        name: companyName,
        email: text(record.email, ""),
        phone: text(record.phone, ""),
        address: text(record.address, ""),
        website: text(record.website, ""),
        contact_name: preparedFor,
        prepared_for: preparedFor,
    };
}

function drawCustomerPdfHeader(doc, font, { customerDetails = {}, customerName = "", generatedAt }) {
    const dark = [24, 31, 38];
    const muted = [91, 101, 113];
    const details = buildCustomerPdfDetails({
        customer: customerDetails,
        customerName,
        contacts: customerDetails.contacts,
    });
    const companyName = text(details.company || details.name || customerName, "Customer");

    doc.setFont(font, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...dark);
    doc.text("PRELIMINARY PROJECT ESTIMATE", PAGE_WIDTH - MARGIN, 22, { align: "right" });
    doc.setFont(font, "normal");
    doc.setFontSize(9);
    doc.setTextColor(...muted);
    doc.text(`Prepared ${generatedAt}`, PAGE_WIDTH - MARGIN, 29, { align: "right" });

    let headerY = 35;
    doc.setFont(font, "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...dark);
    doc.text(companyName, PAGE_WIDTH - MARGIN, headerY, { align: "right" });
    headerY += 4.2;

    const detailLines = [
        details.email ? `Email: ${details.email}` : null,
        details.phone ? `Phone: ${details.phone}` : null,
        details.address ? `Address: ${details.address}` : null,
        details.website ? `Website: ${details.website}` : null,
    ].filter(Boolean);

    doc.setFont(font, "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    if (detailLines.length) {
        const detailText = doc.splitTextToSize(detailLines.join("\n"), 76);
        doc.text(detailText, PAGE_WIDTH - MARGIN, headerY, { align: "right" });
        headerY += detailText.length * 3.2 + 1.5;
    }

    if (details.prepared_for) {
        doc.setFont(font, "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(...dark);
        doc.text(`Prepared for: ${details.prepared_for}`, PAGE_WIDTH - MARGIN, headerY, { align: "right" });
        headerY += 4.5;
    }

    return headerY;
}

function money(value, currency) {
    return formatMoney(Number(value) || 0, currency || "USD");
}

function hours(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? `${n.toLocaleString()} hr` : "—";
}

function cleanFilename(value) {
    return text(value, "internal-quote")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 70) || "internal-quote";
}

/** Keep download in the same user-gesture turn; avoid doc.save() after long awaits when possible. */
function downloadPdf(doc, filename) {
    const safeName = String(filename || "quote.pdf").endsWith(".pdf") ? String(filename) : `${filename}.pdf`;
    try {
        const blob = doc.output("blob");
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = safeName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
        doc.save(safeName);
    }
}

function plannerRows(groups, depth = 0, rows = []) {
    for (const group of groups || []) {
        if (group.enabled === false) continue;
        const quote = groupQuoteOwn(group);
        rows.push({
            label: `${"  ".repeat(depth)}${text(group.label, "Unnamed work package")}`,
            kind: "Work package",
            hours: quote.laborHours || 0,
            rate: quote.profile?.hourlyRate || 0,
            amount: quote.cost || 0,
            purchases: quote.purchases || 0,
            notes: text(group.scope_notes, ""),
        });
        for (const item of group.items || []) {
            if (!String(item.title || "").trim()) continue;
            rows.push({
                label: `${"  ".repeat(depth + 1)}${item.title}`,
                kind: item.item_type || "Planner item",
                hours: 0,
                rate: 0,
                amount: 0,
                purchases: 0,
                notes: text(item.body, ""),
            });
        }
        plannerRows(
            (groups || []).filter((child) => child.parent_id === group.id),
            depth + 1,
            rows
        );
    }
    return rows;
}

function componentRows(components, depth = 0, rows = []) {
    for (const component of components || []) {
        const isRoot = depth === 0;
        const isSummary = isRoot || component.lineKind === "component";
        rows.push({
            label: `${"  ".repeat(depth)}${text(component.title, "Unnamed component")}${component.hideFromCustomer || component.customerVisible === false ? " (internal only)" : ""}`,
            kind: component.hideFromCustomer || component.customerVisible === false
                ? `${component.lineKind || "component"} · internal`
                : component.lineKind || "component",
            hours: isSummary ? component.laborHours : component.ownHours,
            rate: component.quoteHourlyRate,
            labor: isSummary ? component.laborCost : component.ownCost,
            purchases: isSummary ? component.purchases : component.ownPurchases,
            purchasesCustomer: isSummary ? component.purchasesCustomer : component.ownPurchasesCustomer,
            customerPurchases: isSummary ? component.customerPurchases : component.ownCustomerPurchases,
            target: isRoot ? component.target : null,
            variance: isRoot ? component.variance : null,
            contingencyPct: Number(component.contingencyPct) > 0 ? Number(component.contingencyPct) : 0,
            treatment: "",
        });
        for (const child of component.children || []) {
            componentRows([child], depth + 1, rows);
        }
    }
    return rows;
}

function riskLoadedTotal(row) {
    const base = (Number(row.labor) || 0) + (Number(row.purchasesCustomer) || 0);
    return base * (1 + (Number(row.contingencyPct) || 0) / 100);
}

function addPageDecor(doc, title, footerNote = "INTERNAL USE ONLY") {
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setDrawColor(215, 220, 228);
        doc.line(MARGIN, 287, PAGE_WIDTH - MARGIN, 287);
        doc.setFontSize(8);
        doc.setTextColor(105, 112, 124);
        doc.text(footerNote.slice(0, 110), MARGIN, 292);
        doc.text(title.slice(0, 75), PAGE_WIDTH / 2, 292, { align: "center" });
        doc.text(`Page ${page} of ${pageCount}`, PAGE_WIDTH - MARGIN, 292, { align: "right" });
    }
}

function projectValueBreakdown(totals, wixComponents, pricingSettings = {}) {
    const customerSupplied = (wixComponents || []).reduce(
        (sum, component) => sum + (Number(component.customerSupplied ?? component.customerPurchases) || 0),
        0
    );
    const { robot: robotCost, other: otherCustomerPurchases } = customerPurchaseBreakdownFromWixComponents(
        wixComponents || []
    );
    const pbrTotalExact = Number(totals?.customer_total_exact) || 0;
    const pbrTotalRounded = Number(totals?.customer_total_rounded) || 0;
    const roughExact = pbrTotalExact + customerSupplied;
    const roughRounded = applyCustomerRounding(
        roughExact,
        pricingSettings.customer_total_rounding || "nearest_100"
    );
    return {
        customerSupplied,
        robotCost,
        otherCustomerPurchases,
        pbrTotalExact,
        pbrTotalRounded,
        roughExact,
        roughRounded,
        hasCustomerEquipment: customerSupplied > 0,
        projectValueLessRobot: roughExact - robotCost,
    };
}

function sectionHeading(doc, label, y, font = PDF_FONT) {
    doc.setFont(font, "bold");
    doc.setFontSize(12);
    doc.setTextColor(17, 17, 17);
    doc.text(label, MARGIN, y);
    doc.setDrawColor(133, 188, 110);
    doc.setLineWidth(0.7);
    doc.line(MARGIN, y + 2, MARGIN + 34, y + 2);
    return y + 8;
}

function startCustomerContinuationPage(doc, green) {
    doc.addPage();
    // Keep every page white for normal office printing and to match the
    // white background in the Point Blank Robotics logo artwork.
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, "F");
    doc.setFillColor(...green);
    doc.rect(0, 0, PAGE_WIDTH, 7, "F");
    return CUSTOMER_PAGE_TOP;
}

/** If the next block won't fit, start a new page so headers/tables aren't orphaned. */
function ensureCustomerSpace(doc, y, neededMm, green) {
    if (y + neededMm <= CUSTOMER_CONTENT_BOTTOM) return y;
    return startCustomerContinuationPage(doc, green);
}

function estimateTableHeight(rowCount, { head = 9, row = 9, minRows = 1 } = {}) {
    return head + Math.max(minRows, rowCount) * row;
}

function customerTableMargin() {
    return { left: MARGIN, right: MARGIN, bottom: PAGE_HEIGHT - CUSTOMER_CONTENT_BOTTOM };
}

function internalTableMargin() {
    return {
        left: MARGIN,
        right: MARGIN,
        top: INTERNAL_PAGE_TOP,
        bottom: PAGE_HEIGHT - INTERNAL_CONTENT_BOTTOM,
    };
}

function startInternalContinuationPage(doc) {
    doc.addPage();
    return INTERNAL_PAGE_TOP;
}

function ensureInternalSpace(doc, y, neededMm) {
    if (y + neededMm <= INTERNAL_CONTENT_BOTTOM) return y;
    return startInternalContinuationPage(doc);
}

function addWrappedBlock(doc, label, value, y) {
    if (!value || value === "—") return y;
    doc.setFont(PDF_FONT, "bold");
    doc.setFontSize(9);
    doc.setTextColor(55, 65, 81);
    doc.text(label, MARGIN, y);
    doc.setFont(PDF_FONT, "normal");
    doc.setTextColor(75, 85, 99);
    const lines = doc.splitTextToSize(String(value), CONTENT_WIDTH - 42);
    doc.text(lines, MARGIN + 42, y);
    return y + Math.max(6, lines.length * 4.2);
}

export async function exportInternalQuotePdf({
    form = {},
    customerName = "",
    projectName = "",
    estimateLines = [],
    plannerGroups = [],
    wixComponents = [],
    wixJob = {},
    pricingSettings = {},
    totals: totalsPassed,
}) {
    const currency = pricingSettings.currency || "USD";
    const totals =
        totalsPassed ||
        computeQuoteTotals({
            material_cost_at_purchase: form.material_cost_at_purchase,
            lines: estimateLines,
            settings: pricingSettings,
        });
    const plannerTotals = rollupPlannerQuotes(plannerGroups);
    const plannerBase = wixComponents.reduce((sum, component) => sum + (Number(component.builtUp) || 0), 0);
    const jobContingencyPct = Number(wixJob?.contingencyPct) > 0 ? Number(wixJob.contingencyPct) : 0;
    const plannerRiskLoaded = plannerBase * (1 + jobContingencyPct / 100);
    const customerSupplied = wixComponents.reduce((sum, component) => sum + (Number(component.customerPurchases) || 0), 0);
    const roughProjectValue = (Number(totals.customer_total_exact) || 0) + customerSupplied;
    const robotCost = robotCostFromWixComponents(wixComponents);
    const otherCustomerPurchases = customerPurchaseBreakdownFromWixComponents(wixComponents).other;
    const projectValueLessRobot = roughProjectValue - robotCost;
    const economics = internalQuoteEconomicsFromComponents(wixComponents, {
        customerTotal: Number(totals.customer_total_exact) || 0,
        actualHours: wixJob?.actualHours,
        extraMaterialCost: wixJob?.extraMaterialCost,
    });
    const title = text(form.title, "Untitled quote");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const font = await loadK2DFont(doc);
    const logoDataUrl = await loadPdfLogoDataUrl();
    const generatedAt = new Date().toLocaleString();
    const disclaimer = pricingSettings.customer_disclaimer || DEFAULT_CUSTOMER_DISCLAIMER;

    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, PAGE_WIDTH, 40, "F");
    doc.setFillColor(133, 188, 110);
    doc.rect(0, 0, PAGE_WIDTH, 7, "F");
    const logoBottom = drawPdfLogo(doc, logoDataUrl, { x: MARGIN, y: 12, widthMm: 70 });
    if (!logoDataUrl) {
        doc.setTextColor(17, 17, 17);
        doc.setFont(font, "bold");
        doc.setFontSize(16);
        doc.text("POINT BLANK ROBOTICS", MARGIN, 20);
    }
    doc.setFont(font, "bold");
    doc.setFontSize(10);
    doc.setTextColor(91, 101, 113);
    doc.text("Internal estimate review", MARGIN, Math.max(logoBottom + 5, 29));
    doc.setTextColor(17, 17, 17);
    doc.setFontSize(11);
    doc.text("INTERNAL USE", PAGE_WIDTH - MARGIN, 20, { align: "right" });
    doc.setFont(font, "normal");
    doc.setFontSize(9);
    doc.setTextColor(91, 101, 113);
    doc.text(title.slice(0, 75), PAGE_WIDTH - MARGIN, 27, { align: "right" });
    doc.text(`Generated ${generatedAt}`, PAGE_WIDTH - MARGIN, 33, { align: "right" });

    let y = 52;
    doc.setTextColor(17, 17, 17);
    doc.setFont(font, "bold");
    doc.setFontSize(14);
    doc.text("Quote overview", MARGIN, y);
    y += 8;
    autoTable(doc, {
        startY: y,
        margin: internalTableMargin(),
        theme: "grid",
        rowPageBreak: "avoid",
        headStyles: { font, fillColor: [133, 188, 110], textColor: [17, 17, 17], fontStyle: "bold" },
        styles: { font, fontSize: 9, cellPadding: 3, textColor: [55, 65, 81], lineColor: [218, 224, 218], lineWidth: 0.25 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 45 }, 1: { cellWidth: 51 }, 2: { fontStyle: "bold", cellWidth: 45 }, 3: { cellWidth: 35 } },
        body: [
            ["Customer", text(customerName), "Status", text(form.status)],
            ["Project", text(projectName), "Currency", currency],
            ["Services subtotal", money(totals.services_subtotal, currency), "PBR materials cost", money(totals.materials_cost_basis, currency)],
            ["PBR materials charge", money(totals.materials_customer, currency), "Material markup", `${totals.materials_markup_percent}%`],
            ["Contingency", `${totals.contingency_percent}% · ${money(totals.contingency_amount, currency)}`, "Tax", `${totals.tax_percent}% · ${money(totals.tax_amount, currency)}`],
            ["Exact modeled total", money(totals.customer_total_exact, currency), "Rounded ballpark", money(totals.customer_total_rounded, currency)],
            ...(wixComponents.length
                ? [
                    ["Out of pocket (PBR buys + internal labor)", money(economics.trueCost, currency), "Gross profit", money(economics.grossProfit, currency)],
                    ["Margin", `${Number(economics.marginPct).toFixed(1)}% of customer total`, "Markup on cost", `${Number(economics.markupOnCostPct).toFixed(1)}%`],
                    ["Material markup", `${money(economics.materialMarkupAmount, currency)} (${Number(economics.materialMarkupPercent).toFixed(1)}%)`, "Internal-only absorbed", money(economics.absorbedInternal, currency)],
                    ["Hours logged vs quoted", `${Number(economics.actualHours || 0)} / ${Number(economics.laborHours || 0)} hrs`, "Extra materials logged", money(economics.extraMaterialCost, currency)],
                    ["Customer-facing PBR buy cost", money(economics.customerPurchaseCost, currency), "Internal-only PBR buys", money(economics.internalPurchaseCost, currency)],
                    ["Customer-supplied value", money(customerSupplied, currency), "Rough project value", money(roughProjectValue, currency)],
                    ["  Robot cost (included above)", money(robotCost, currency), "Project value less robot cost", money(projectValueLessRobot, currency)],
                    ["  Other customer purchases (included above)", money(otherCustomerPurchases, currency), "Customer purchase total", money(customerSupplied, currency)],
                ]
                : []),
            ...(wixJob?.contingencyPct
                ? [["Planner job contingency", `${jobContingencyPct}%`, "Risk-loaded planner scope", money(plannerRiskLoaded, currency)]]
                : []),
        ],
    });

    y = doc.lastAutoTable.finalY + 12;
    y = ensureInternalSpace(doc, y, 28);
    y = sectionHeading(doc, "Estimate line builder", y, font);
    autoTable(doc, {
        startY: y,
        margin: internalTableMargin(),
        theme: "striped",
        rowPageBreak: "avoid",
        showHead: "everyPage",
        headStyles: { font, fillColor: [133, 188, 110], textColor: [17, 17, 17], fontStyle: "bold" },
        styles: { font, fontSize: 9, cellPadding: 3, overflow: "linebreak", lineColor: [218, 224, 218], lineWidth: 0.25 },
        head: [["Scope / task", "Qty", "Unit", "Rate", "Extension"]],
        body: estimateLines.length
            ? estimateLines.map((line) => [
                text(line.label, "Unlabeled line"),
                Number(line.qty) || 0,
                text(line.unit, "hour"),
                money(line.bill_rate, currency),
                money(lineExtension(line), currency),
            ])
            : [["No estimate lines entered", "", "", "", ""]],
        columnStyles: { 0: { cellWidth: 76 }, 1: { halign: "right", cellWidth: 19 }, 2: { cellWidth: 20 }, 3: { halign: "right", cellWidth: 30 }, 4: { halign: "right", cellWidth: 32 } },
    });

    y = doc.lastAutoTable.finalY + 12;
    const plannerDetailRows = plannerRows((plannerGroups || []).filter((group) => !group.parent_id));
    if (plannerDetailRows.length) {
        y = ensureInternalSpace(doc, y, 28);
        y = sectionHeading(doc, "Planner hierarchy and built-up economics", y, font);
        autoTable(doc, {
            startY: y,
            margin: internalTableMargin(),
            theme: "striped",
            rowPageBreak: "avoid",
            showHead: "everyPage",
            headStyles: { font, fillColor: [133, 188, 110], textColor: [17, 17, 17] },
            styles: { font, fontSize: 8, cellPadding: 2.3, overflow: "linebreak" },
            head: [["Work package / planner item", "Type", "Hours", "Rate", "Labor", "Purchases", "Notes"]],
            body: plannerDetailRows.map((row) => [
                row.label,
                row.kind,
                row.hours ? hours(row.hours) : "—",
                row.rate ? money(row.rate, currency) : "—",
                row.amount ? money(row.amount, currency) : "—",
                row.purchases ? money(row.purchases, currency) : "—",
                row.notes,
            ]),
            columnStyles: {
                0: { cellWidth: 48 },
                1: { cellWidth: 22 },
                2: { halign: "right", cellWidth: 20 },
                3: { halign: "right", cellWidth: 20 },
                4: { halign: "right", cellWidth: 24 },
                5: { halign: "right", cellWidth: 24 },
                6: { cellWidth: 28 },
            },
        });

        y = doc.lastAutoTable.finalY + 12;
    }
    if (wixComponents.length) {
        y = ensureInternalSpace(doc, y, 24);
        y = sectionHeading(doc, "Imported website component detail", y, font);
        const nestedComponents = wixComponents.flatMap((root) =>
            (root.children || []).filter((child) => child.lineKind !== "labor" && child.lineKind !== "purchase")
        );
        const subProjects = nestedComponents.length ? nestedComponents : wixComponents;
        const sharedRows = nestedComponents.length
            ? wixComponents.flatMap((root) =>
                (root.children || []).filter((child) => child.lineKind !== "component")
            )
            : [];
        const tableColumns = [
            ["Component / child line", "Type", "Hrs", "Labor", "PBR cost / charge", "Cust. supplied", "Risk", "Target"],
            {
                0: { cellWidth: 48 },
                1: { cellWidth: 18 },
                2: { halign: "right", cellWidth: 14 },
                3: { halign: "right", cellWidth: 18 },
                4: { halign: "right", cellWidth: 28 },
                5: { halign: "right", cellWidth: 22 },
                6: { halign: "right", cellWidth: 18 },
                7: { halign: "right", cellWidth: 16 },
            },
        ];
        const renderComponentTable = (rows) => {
            autoTable(doc, {
                startY: y,
                margin: internalTableMargin(),
                theme: "plain",
                pageBreak: "auto",
                rowPageBreak: "avoid",
                showHead: "everyPage",
                headStyles: { font, fillColor: [133, 188, 110], textColor: [17, 17, 17], fontStyle: "bold", cellPadding: 2.8 },
                styles: {
                    font,
                    fontSize: 7.8,
                    cellPadding: 2.6,
                    overflow: "linebreak",
                    textColor: [55, 65, 81],
                    lineColor: [218, 224, 218],
                    lineWidth: { top: 0, right: 0, bottom: 0.25, left: 0 },
                },
                alternateRowStyles: { fillColor: [255, 255, 255] },
                head: [tableColumns[0]],
                body: rows.length
                    ? rows.map((row) => [
                        row.label,
                        row.kind,
                        row.hours ? hours(row.hours) : "—",
                        row.labor ? money(row.labor, currency) : "—",
                        row.purchases || row.purchasesCustomer
                            ? `${money(row.purchases, currency)} / ${money(row.purchasesCustomer, currency)}`
                            : "—",
                        row.customerPurchases ? money(row.customerPurchases, currency) : "—",
                        row.contingencyPct
                            ? `${row.contingencyPct}%\n+${money(riskLoadedTotal(row) - ((Number(row.labor) || 0) + (Number(row.purchasesCustomer) || 0)), currency)}\n${money(riskLoadedTotal(row), currency)} loaded`
                            : "—",
                        row.target == null
                            ? "—"
                            : `${money(row.target, currency)}\n${row.variance >= 0 ? "+" : ""}${money(row.variance, currency)}`,
                    ])
                    : [["No child lines listed", "—", "—", "—", "—", "—", "—", "—"]],
                columnStyles: tableColumns[1],
            });
            y = doc.lastAutoTable.finalY + 10;
        };
        const renderSubProjectCard = (heading, meta, rows) => {
            y = ensureInternalSpace(doc, y, 42);
            doc.setFillColor(133, 188, 110);
            doc.rect(MARGIN, y, CONTENT_WIDTH, 12, "F");
            doc.setFont(font, "bold");
            doc.setFontSize(11);
            doc.setTextColor(17, 17, 17);
            doc.text(heading, MARGIN + 5, y + 5.5);
            if (meta) {
                doc.setFont(font, "normal");
                doc.setFontSize(8);
                doc.text(meta, PAGE_WIDTH - MARGIN - 5, y + 5.5, { align: "right" });
            }
            y += 16;
            renderComponentTable(rows);
        };
        if (nestedComponents.length) {
            y = ensureInternalSpace(doc, y, 12);
            doc.setFont(font, "bold");
            doc.setFontSize(10);
            doc.setTextColor(105, 112, 124);
            doc.text(`Project scope: ${wixComponents.map((root) => text(root.title, "Unnamed project")).join(", ")}`, MARGIN, y);
            y += 7;
        }
        subProjects.forEach((component, componentIndex) => {
            const componentContingencyPct = Number(component.contingencyPct) > 0 ? Number(component.contingencyPct) : 0;
            const componentContingencyAmount = (Number(component.builtUp) || 0) * (componentContingencyPct / 100);
            renderSubProjectCard(
                `SUB-PROJECT ${componentIndex + 1} · ${text(component.title, "Unnamed component")}`,
                `PBR ${money(component.builtUp, currency)}${componentContingencyPct ? `  ·  ${componentContingencyPct}% risk (+${money(componentContingencyAmount, currency)})` : ""}  ·  Customer-provided ${money(component.customerPurchases, currency)}`,
                componentRows(component.children || [])
            );
        });
        if (sharedRows.length) {
            renderSubProjectCard(
                `PARENT SCOPE · ${text(wixComponents[0]?.title, "Shared scope")}`,
                "",
                componentRows(sharedRows)
            );
        }
    }

    if (plannerDetailRows.length) {
        y = ensureInternalSpace(doc, y, 28);
        y = sectionHeading(doc, "Planner roll-up", y, font);
        autoTable(doc, {
            startY: y,
            margin: internalTableMargin(),
            theme: "grid",
            rowPageBreak: "avoid",
            headStyles: { font, fillColor: [242, 242, 242], textColor: [17, 17, 17] },
            styles: { font, fontSize: 9, cellPadding: 3 },
            body: [
                ["Planner labor", `${hours(plannerTotals.laborHours)} · ${money(plannerTotals.cost, currency)}`],
                ["Planner purchases", money(plannerTotals.purchases, currency)],
                ["Planner built-up total", money(plannerTotals.total, currency)],
                ["Contingency / risk", `${plannerTotals.contingencyPct || 0}%`],
            ],
            columnStyles: { 0: { fontStyle: "bold", cellWidth: 70 }, 1: { halign: "right" } },
        });

        y = doc.lastAutoTable.finalY + 12;
    }
    y = ensureInternalSpace(doc, y, 24);
    y = sectionHeading(doc, "Scope notes and assumptions", y, font);
    const blocks = [
        ["Shop / environment", form.shop_type],
        ["Robot implementation", form.robot_implementation],
        ["Estimated time", form.estimated_time],
        ["Travel", form.travel],
        ["Materials needed", form.materials_needed],
        ["Customer materials / actions", form.customer_material_actions],
        ["Scheduling / planning", form.scheduling_planning],
        ["Software requirements", form.software_requirements],
        ["Customer equipment", form.customer_equipment],
        ["Other notes / exclusions", form.other_notes],
        ["Quote terms", disclaimer],
    ];
    for (const [label, value] of blocks) {
        if (!value || value === "—") continue;
        const wrapped = doc.splitTextToSize(String(value), CONTENT_WIDTH - 42);
        const blockH = Math.max(6, wrapped.length * 4.2) + 2;
        y = ensureInternalSpace(doc, y, blockH + 8);
        if (y === INTERNAL_PAGE_TOP) {
            y = sectionHeading(doc, "Scope notes and assumptions (continued)", y, font);
        }
        y = addWrappedBlock(doc, label, value, y);
    }

    addPageDecor(doc, title, `INTERNAL USE ONLY · ${disclaimer}`);
    downloadPdf(doc, `${cleanFilename(title)}-internal-report.pdf`);
}

export async function exportCustomerQuotePdf({
    form = {},
    customerName = "",
    customerDetails = {},
    projectName = "",
    estimateLines = [],
    wixComponents = [],
    pricingSettings = {},
    totals: totalsPassed,
}) {
    const currency = pricingSettings.currency || "USD";
    const totals =
        totalsPassed ||
        computeQuoteTotals({
            material_cost_at_purchase: form.material_cost_at_purchase,
            lines: estimateLines,
            settings: pricingSettings,
        });
    const title = text(projectName || form.title, "Project quote");
    const value = projectValueBreakdown(totals, wixComponents, pricingSettings);
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const font = await loadK2DFont(doc);
    const logoDataUrl = await loadPdfLogoDataUrl();
    const generatedAt = new Date().toLocaleDateString();
    const disclaimer = pricingSettings.customer_disclaimer || DEFAULT_CUSTOMER_DISCLAIMER;
    const green = [133, 188, 110];
    const dark = [24, 31, 38];
    const muted = [91, 101, 113];

    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, PAGE_WIDTH, 297, "F");
    doc.setFillColor(...green);
    doc.rect(0, 0, PAGE_WIDTH, 7, "F");
    const logoBottom = drawPdfLogo(doc, logoDataUrl, { x: MARGIN, y: 12, widthMm: 105 });
    if (!logoDataUrl) {
        doc.setTextColor(...dark);
        doc.setFont(font, "bold");
        doc.setFontSize(24);
        doc.text("POINT BLANK ROBOTICS", MARGIN, 25);
    }
    const headerBottom = drawCustomerPdfHeader(doc, font, { customerDetails, customerName, generatedAt });

    let y = Math.max(logoBottom + 10, headerBottom + 4);
    doc.setTextColor(...dark);
    doc.setFont(font, "bold");
    doc.setFontSize(18);
    doc.text(title.slice(0, 90), MARGIN, y);
    y += 8;
    y += 6;

    const heroHeight = value.hasCustomerEquipment ? 46 : 34;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(220, 226, 219);
    doc.rect(MARGIN, y, CONTENT_WIDTH, heroHeight, "FD");
    if (value.hasCustomerEquipment) {
        doc.setTextColor(...muted);
        doc.setFont(font, "normal");
        doc.setFontSize(9);
        doc.text("TOTAL PROJECT BUDGET", MARGIN + 8, y + 10);
        doc.setTextColor(...dark);
        doc.setFont(font, "bold");
        doc.setFontSize(25);
        doc.text(money(value.roughRounded, currency), MARGIN + 8, y + 24);
        doc.setFont(font, "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...muted);
        doc.text("Point Blank Robotics services", PAGE_WIDTH - MARGIN - 8, y + 12, { align: "right" });
        doc.setTextColor(...dark);
        doc.setFont(font, "bold");
        doc.text(money(value.pbrTotalRounded, currency), PAGE_WIDTH - MARGIN - 8, y + 17, { align: "right" });
        doc.setFont(font, "normal");
        doc.setTextColor(...muted);
        doc.text("Customer-supplied equipment", PAGE_WIDTH - MARGIN - 8, y + 24, { align: "right" });
        doc.setTextColor(...dark);
        doc.setFont(font, "bold");
        doc.text(money(value.customerSupplied, currency), PAGE_WIDTH - MARGIN - 8, y + 29, { align: "right" });
        if (value.robotCost > 0) {
            doc.setFont(font, "normal");
            doc.setFontSize(7.5);
            doc.setTextColor(...muted);
            doc.text(
                `incl. robot hardware ${money(value.robotCost, currency)}`,
                PAGE_WIDTH - MARGIN - 8,
                y + 34,
                { align: "right" }
            );
        }
        doc.setFont(font, "normal");
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        doc.text(`Currency: ${currency}`, PAGE_WIDTH - MARGIN - 8, y + heroHeight - 4, { align: "right" });
    } else {
        doc.setTextColor(...muted);
        doc.setFontSize(9);
        doc.text("ESTIMATED PROJECT TOTAL", MARGIN + 8, y + 10);
        doc.setTextColor(...dark);
        doc.setFont(font, "bold");
        doc.setFontSize(25);
        doc.text(money(value.pbrTotalRounded, currency), MARGIN + 8, y + 23);
        doc.setFont(font, "normal");
        doc.setFontSize(9);
        doc.setTextColor(...muted);
        doc.text("Point Blank Robotics services", PAGE_WIDTH - MARGIN - 8, y + 18, { align: "right" });
        doc.text(`Currency: ${currency}`, PAGE_WIDTH - MARGIN - 8, y + 25, { align: "right" });
    }
    y += heroHeight + 13;

    y = sectionHeading(doc, value.hasCustomerEquipment ? "Point Blank Robotics quote summary" : "Quote summary", y, font);
    const summaryRows = [
        ["Services and project scope", money(totals.services_subtotal, currency)],
    ];
    if (Number(totals.materials_customer) > 0) {
        summaryRows.push(["Materials and equipment", money(totals.materials_customer, currency)]);
    }
    if (Number(totals.contingency_amount) > 0) {
        summaryRows.push(["Contingency allowance", money(totals.contingency_amount, currency)]);
    }
    if (totals.tax_percent > 0) summaryRows.push(["Tax", money(totals.tax_amount, currency)]);
    y = ensureCustomerSpace(doc, y, estimateTableHeight(summaryRows.length) + 20, green);
    autoTable(doc, {
        startY: y,
        margin: customerTableMargin(),
        theme: "grid",
        pageBreak: "avoid",
        rowPageBreak: "avoid",
        styles: { font, fontSize: 10, cellPadding: 3, textColor: dark },
        columnStyles: { 0: { cellWidth: 125 }, 1: { halign: "right", cellWidth: 57 } },
        body: summaryRows,
        willDrawCell: (data) => {
            if (data.section === "body" && data.row.index % 2 === 0) {
                doc.setFillColor(255, 255, 255);
                doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, "F");
            }
        },
    });
    y = doc.lastAutoTable.finalY + 4;
    doc.setDrawColor(...green);
    doc.setLineWidth(0.8);
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
    y += 8;
    doc.setFont(font, "bold");
    doc.setFontSize(12);
    if (value.hasCustomerEquipment) {
        doc.text("Point Blank Robotics total", MARGIN, y);
        doc.text(money(value.pbrTotalRounded, currency), PAGE_WIDTH - MARGIN, y, { align: "right" });
        y += 10;
        doc.setFont(font, "normal");
        doc.setFontSize(10);
        doc.setTextColor(...muted);
        doc.text("Customer-supplied equipment (purchased directly by customer)", MARGIN, y);
        doc.setTextColor(...dark);
        doc.setFont(font, "bold");
        doc.text(money(value.customerSupplied, currency), PAGE_WIDTH - MARGIN, y, { align: "right" });
        if (value.robotCost > 0) {
            y += 7;
            doc.setFont(font, "normal");
            doc.setFontSize(9);
            doc.setTextColor(...muted);
            doc.text("  Robot hardware (included above)", MARGIN, y);
            doc.text(money(value.robotCost, currency), PAGE_WIDTH - MARGIN, y, { align: "right" });
        }
        y += 10;
        doc.setDrawColor(...green);
        doc.setLineWidth(0.8);
        doc.line(MARGIN, y - 3, PAGE_WIDTH - MARGIN, y - 3);
        doc.setFont(font, "bold");
        doc.setFontSize(12);
        doc.setTextColor(...dark);
        doc.text("Total project budget", MARGIN, y + 4);
        doc.text(money(value.roughRounded, currency), PAGE_WIDTH - MARGIN, y + 4, { align: "right" });
        y += 14;
    } else {
        doc.text("Total project investment", MARGIN, y);
        doc.text(money(value.pbrTotalRounded, currency), PAGE_WIDTH - MARGIN, y, { align: "right" });
        y += 15;
    }

    const presentation = customerQuotePresentationFromComponents(wixComponents);
    const hasPackages = presentation.packages.length > 0;

    if (hasPackages) {
        y = ensureCustomerSpace(doc, y, 28, green);
        y = sectionHeading(doc, "Included scope", y, font);
        if (presentation.parentTitles.length) {
            doc.setFont(font, "normal");
            doc.setFontSize(9);
            doc.setTextColor(...muted);
            doc.text(`Project scope: ${presentation.parentTitles.join(", ")}`, MARGIN, y);
            y += 7;
        }

        for (const pkg of presentation.packages) {
            const matchingLine = (estimateLines || []).find(
                (line) => String(line.label || "").trim() === pkg.title
            );
            const amount = matchingLine ? lineExtension(matchingLine) : pkg.amount;
            doc.setFont(font, "bold");
            doc.setFontSize(11);
            const titleLines = doc.splitTextToSize(text(pkg.title, "Work package"), CONTENT_WIDTH - 48);
            doc.setFont(font, "normal");
            doc.setFontSize(9.5);
            const includedBlocks = (pkg.included || []).map((item) =>
                doc.splitTextToSize(item, CONTENT_WIDTH - 8)
            );
            const includedH = includedBlocks.reduce((sum, lines) => sum + lines.length * 4.6, 0);
            const blockH = Math.max(8, titleLines.length * 5.2) + includedH + 8;
            y = ensureCustomerSpace(doc, y, Math.min(blockH, 40), green);

            doc.setFont(font, "bold");
            doc.setFontSize(11);
            doc.setTextColor(...dark);
            doc.text(titleLines, MARGIN, y);
            doc.text(money(amount, currency), PAGE_WIDTH - MARGIN, y, { align: "right" });
            y += Math.max(7, titleLines.length * 5.2);

            doc.setFont(font, "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(...muted);
            for (const lines of includedBlocks) {
                y = ensureCustomerSpace(doc, y, lines.length * 4.6 + 1, green);
                doc.text(lines, MARGIN + 3, y);
                y += lines.length * 4.6;
            }
            y += 6;
        }

        const packageTitles = new Set(presentation.packages.map((pkg) => pkg.title));
        const extraLines = (estimateLines || []).filter((line) => {
            const label = String(line.label || "").trim();
            return label && !packageTitles.has(label) && lineExtension(line) > 0;
        });
        for (const line of extraLines) {
            y = ensureCustomerSpace(doc, y, 10, green);
            doc.setFont(font, "bold");
            doc.setFontSize(11);
            doc.setTextColor(...dark);
            const titleLines = doc.splitTextToSize(text(line.label, "Additional scope"), CONTENT_WIDTH - 48);
            doc.text(titleLines, MARGIN, y);
            doc.text(money(lineExtension(line), currency), PAGE_WIDTH - MARGIN, y, { align: "right" });
            y += Math.max(7, titleLines.length * 5.2) + 4;
        }
        y += 6;
    } else {
        const customerLines = estimateLines.filter((line) => String(line.label || "").trim());
        if (customerLines.length) {
            y = ensureCustomerSpace(doc, y, 8 + estimateTableHeight(customerLines.length, { head: 10, row: 10 }) + 14, green);
            y = sectionHeading(doc, "Included scope", y, font);
            autoTable(doc, {
                startY: y,
                margin: customerTableMargin(),
                theme: "grid",
                pageBreak: "avoid",
                rowPageBreak: "avoid",
                headStyles: { font, fillColor: green, textColor: dark, fontStyle: "bold" },
                styles: { font, fontSize: 9, cellPadding: 3, overflow: "linebreak" },
                head: [["Scope / deliverable", "Amount"]],
                body: customerLines.map((line) => [
                    text(line.label, "Included project scope"),
                    money(lineExtension(line), currency),
                ]),
                columnStyles: { 0: { cellWidth: 140 }, 1: { halign: "right", cellWidth: 42 } },
            });
            y = doc.lastAutoTable.finalY + 14;
        }
    }

    if (presentation.customerItems.length) {
        y = ensureCustomerSpace(doc, y, 8 + estimateTableHeight(presentation.customerItems.length, { head: 10, row: 9 }) + 12, green);
        y = sectionHeading(doc, "Customer-supplied equipment", y, font);
        autoTable(doc, {
            startY: y,
            margin: customerTableMargin(),
            theme: "grid",
            pageBreak: "avoid",
            rowPageBreak: "avoid",
            headStyles: { font, fillColor: green, textColor: dark, fontStyle: "bold" },
            styles: { font, fontSize: 9, cellPadding: 3, overflow: "linebreak" },
            head: [["Equipment / purchase", "Amount"]],
            body: presentation.customerItems.map((item) => [
                text(item.title, "Customer purchase"),
                money(item.amount, currency),
            ]),
            columnStyles: { 0: { cellWidth: 140 }, 1: { halign: "right", cellWidth: 42 } },
        });
        y = doc.lastAutoTable.finalY + 12;
    }

    if (wixComponents.length && value.hasCustomerEquipment) {
        const valueRows = value.robotCost > 0 ? 4 : 3;
        y = ensureCustomerSpace(doc, y, 8 + estimateTableHeight(valueRows) + 12, green);
        y = sectionHeading(doc, "Project value detail", y, font);
        const valueBody = [
            ["Point Blank Robotics quoted total", money(value.pbrTotalRounded, currency)],
            ["Customer-supplied equipment", money(value.customerSupplied, currency)],
        ];
        if (value.robotCost > 0) {
            valueBody.push(["  Robot hardware (included in customer-supplied total)", money(value.robotCost, currency)]);
        }
        valueBody.push(["Total project budget", money(value.roughRounded, currency)]);
        autoTable(doc, {
            startY: y,
            margin: customerTableMargin(),
            theme: "grid",
            pageBreak: "avoid",
            rowPageBreak: "avoid",
            styles: { font, fontSize: 9, cellPadding: 2.5, textColor: dark },
            body: valueBody,
            columnStyles: { 0: { cellWidth: 130, fontStyle: "bold" }, 1: { halign: "right", cellWidth: 52 } },
        });
        y = doc.lastAutoTable.finalY + 12;
    }

    y = ensureCustomerSpace(doc, y, 36, green);
    y = sectionHeading(doc, "Important notes", y, font);
    const notes = [
        form.estimated_time ? `Estimated schedule: ${form.estimated_time}` : "",
        form.robot_implementation ? "Project scope includes the robotics and integration work described in the agreed scope." : "",
        disclaimer,
    ].filter(Boolean);
    doc.setFont(font, "normal");
    doc.setFontSize(9);
    doc.setTextColor(...muted);
    for (const note of notes) {
        const lines = doc.splitTextToSize(`• ${note}`, CONTENT_WIDTH);
        const blockH = Math.max(6, lines.length * 4.5);
        y = ensureCustomerSpace(doc, y, blockH + 2, green);
        doc.setFont(font, "normal");
        doc.setFontSize(9);
        doc.setTextColor(...muted);
        doc.text(lines, MARGIN, y);
        y += blockH;
    }

    const slogan = text(pricingSettings.brand_slogan, "Automation Simplified. Point Blank.");
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setDrawColor(210, 218, 209);
        doc.line(MARGIN, 281, PAGE_WIDTH - MARGIN, 281);
        doc.setFont(font, "normal");
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        doc.text(`POINT BLANK ROBOTICS · ${disclaimer.slice(0, 86)}`, MARGIN, 288);
        doc.text(`Page ${page} of ${pageCount}`, PAGE_WIDTH - MARGIN, 288, { align: "right" });
        if (page === pageCount) {
            doc.setFont(font, "bold");
            doc.setFontSize(12);
            doc.setTextColor(...green);
            doc.text(slogan, PAGE_WIDTH / 2, 273, { align: "center" });
        }
    }

    downloadPdf(doc, `${cleanFilename(title)}-customer-quote.pdf`);
}

